import styled from '@emotion/styled';
import { gettext } from '@eventbrite/i18n';
import { AlertIcon } from '@eventbrite/marmalade';
import React, { useCallback, useState } from 'react';
import {
    Alert,
    AppWithRouteProps,
    FormHeader,
    LayoutBackground,
    ProgressBar,
} from '../../../shared';
import { LoadingCarouselScreen } from '../../LoadingCarousel';
import { CreateEventForm } from './CreateEventForm';

export enum CreateEventScreenTestId {
    FormContainer = 'AutoCreateEventFormContainer',
}

export const CreateEventScreen: React.FC<AppWithRouteProps> = ({ history }) => {
    const [isLoading, updateIsLoading] = useState(false);
    const [showNotificationError, setShowNotificationError] = useState(false);

    const onSaveStart = useCallback(() => {
        setShowNotificationError(false);
        updateIsLoading(true);
    }, []);

    const onSaveError = useCallback(() => {
        setShowNotificationError(true);
        updateIsLoading(false);
    }, []);

    const onSaveSuccess = useCallback(() => {
        history.push(`/preview`);
    }, [history]);

    return (
        <>
            <LayoutBackground />
            {isLoading && <LoadingCarouselScreen />}
            <FormContainer
                isLoading={isLoading}
                data-testid={CreateEventScreenTestId.FormContainer}
            >
                <FormHeaderContainer>
                    <FormHeader />
                </FormHeaderContainer>
                <FormLayout>
                    {showNotificationError && (
                        <NotificationContainer>
                            <Alert
                                description={gettext(
                                    'We saved your event details, so you can try again.',
                                ).toString()}
                                title={gettext(
                                    'There was an error creating your event.',
                                ).toString()}
                                variant="danger"
                                icon={<AlertIcon />}
                            />
                        </NotificationContainer>
                    )}
                    <ProgressBarContainer>
                        <ProgressBar
                            step={2}
                            totalSteps={3}
                            text={gettext(
                                'Step 2 of 3: Create your event',
                            ).toString()}
                        />
                    </ProgressBarContainer>
                    <CreateEventForm
                        onSaveStart={onSaveStart}
                        onSaveSuccess={onSaveSuccess}
                        onSaveError={onSaveError}
                    />
                </FormLayout>
            </FormContainer>
        </>
    );
};

const FormContainer = styled.div<{ isLoading: boolean }>`
    display: ${({ isLoading }) => (isLoading ? 'none' : 'block')};
    padding: 24px;
`;

const FormHeaderContainer = styled.div`
    padding-bottom: 1.5rem;
`;

const ProgressBarContainer = styled.div`
    margin-bottom: 1.5rem;
`;

const NotificationContainer = styled.div`
    margin-bottom: 1.5rem;
`;

const FormLayout = styled.div`
    margin: 0 auto 40px;
    max-width: 600px;
    padding: 0 0 3.75rem;
    width: 100%;
    z-index: 1;
`;
